import { useState, useMemo } from 'react';
import type { ProjectDetail, ProjectTask } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { updateTaskDescription, uploadTaskDescriptionImage } from '@/api/tasks';
import { RichDescriptionEditor } from './RichDescriptionEditor';
import { SafeHtml } from '@/components/ui/SafeHtml';
import { descriptionHasContent } from '@/utils/descriptionSanitize';

/* ─── Section 1: Projectinfo ─── */

export function ProjectInfoSection({ detail }: { detail: ProjectDetail }) {
  const clientName = detail.saleOrder?.partner_id ? detail.saleOrder.partner_id[1] : '-';
  const soNumber = detail.saleOrder?.name ?? '-';

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h3 className="text-sm font-extrabold text-td-navy mb-4">Projectinfo</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Project</dt>
          <dd className="text-sm font-bold text-td-navy mt-0.5">{detail.projectName}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">S-nummer</dt>
          <dd className="text-sm font-bold text-td-blue mt-0.5">{soNumber}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Klant</dt>
          <dd className="text-sm font-bold text-td-navy mt-0.5">{clientName}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Adres</dt>
          <dd className="text-sm font-bold text-td-navy mt-0.5">{detail.werfLocation || '-'}</dd>
        </div>
      </dl>
    </section>
  );
}

/* ─── Section 2: Taakomschrijving ─── */

interface DeadlineGroup {
  label: string;
  sortKey: string;
  tasks: ProjectTask[];
}

/** Full Dutch date of a deadline ("woensdag 29 april 2026"), or null without one. */
function formatDeadlineLong(dateStr: string | false | undefined): string | null {
  if (!dateStr) return null;
  const clean = dateStr.split(' ')[0]; // Odoo sometimes returns "2026-03-24 16:00:00"
  const d = new Date(clean + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/** The day's date, prominent above the task name: fase day tasks all share the fase's name. */
function TaskDate({ date }: { date: string | null }) {
  if (!date) return null;
  return <p className="text-base font-bold text-td-blue mb-0.5">{date}</p>;
}

function groupTasksByDeadline(tasks: ProjectTask[]): DeadlineGroup[] {
  const map = new Map<string, ProjectTask[]>();
  for (const task of tasks) {
    const raw = task.date_deadline;
    const key = (raw && typeof raw === 'string') ? raw.split(' ')[0] : 'geen-deadline';
    let list = map.get(key);
    if (!list) {
      list = [];
      map.set(key, list);
    }
    list.push(task);
  }

  const groups: DeadlineGroup[] = [];
  for (const [key, gTasks] of map) {
    groups.push({
      label: key === 'geen-deadline' ? 'Geen deadline' : formatDeadlineLong(key) ?? 'Geen deadline',
      sortKey: key === 'geen-deadline' ? '9999-99-99' : key,
      tasks: gTasks,
    });
  }
  groups.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return groups;
}

/** English labels — installer/subcontractor-facing surface. */
const EN_LABELS = {
  label: 'Description',
  placeholder: 'Notes for this task…',
  unsaved: 'Not saved',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Save failed',
  retry: 'Try again',
};

function TaskDescriptionEditable({
  task,
  projectId,
}: {
  task: ProjectTask;
  projectId: number;
}) {
  const handleSaveHtml = async (html: string): Promise<string> => {
    const result = await updateTaskDescription(projectId, task.id, html);
    const savedHtml = result.description;
    useProjectViewStore.getState().applyTaskDescription(projectId, task.id, savedHtml);
    return savedHtml;
  };

  const handleUploadImage = async (file: File) => {
    return uploadTaskDescriptionImage(projectId, task.id, file);
  };

  return (
    <RichDescriptionEditor
      taskKey={task.id}
      valueHtml={task.description || ''}
      onSaveHtml={handleSaveHtml}
      uploadImage={handleUploadImage}
      labels={EN_LABELS}
      id={`task-desc-${task.id}`}
    />
  );
}

export function TaakomschrijvingSection({
  projectTasks,
  projectId,
  task: primaryTask,
}: {
  projectTasks: ProjectTask[];
  projectId: number;
  /** The shown day (pickInstallateurTask); Installateurs shows the same one. */
  task: ProjectTask | null;
}) {
  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canEdit = canFeature('description_edit');
  const [showOthers, setShowOthers] = useState(false);

  const tasksWithDesc = useMemo(
    () => projectTasks.filter((t) => descriptionHasContent(t.description)),
    [projectTasks],
  );

  // Remaining tasks (everything except primary), grouped by deadline
  const otherGroups = useMemo(
    () => groupTasksByDeadline(tasksWithDesc.filter((t) => t.id !== primaryTask?.id)),
    [tasksWithDesc, primaryTask],
  );

  if (!primaryTask && tasksWithDesc.length === 0) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-extrabold text-td-navy mb-4">Taakomschrijving</h3>
        <p className="text-sm text-td-muted italic">Geen taakomschrijvingen beschikbaar</p>
      </section>
    );
  }

  const primaryHasDesc = primaryTask && descriptionHasContent(primaryTask.description);

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h3 className="text-sm font-extrabold text-td-navy mb-4">Taakomschrijving</h3>

      {!primaryTask && (
        <p className="text-sm text-td-muted italic mb-2">Geen taak in deze fase</p>
      )}

      {/* Primary (active) task */}
      {primaryTask && (
        <div>
          <TaskDate date={formatDeadlineLong(primaryTask.date_deadline)} />
          <p className="text-sm font-semibold text-td-navy mb-2">{primaryTask.name}</p>
          {primaryHasDesc ? (
            canEdit ? (
              <TaskDescriptionEditable task={primaryTask} projectId={projectId} />
            ) : (
              <SafeHtml html={primaryTask.description || ''} className="text-sm text-td-navy" />
            )
          ) : (
            canEdit ? (
              <TaskDescriptionEditable task={primaryTask} projectId={projectId} />
            ) : (
              <p className="text-sm text-td-muted italic">Geen omschrijving voor deze taak</p>
            )
          )}
        </div>
      )}

      {/* Toggle for other tasks */}
      {otherGroups.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowOthers(!showOthers)}
            className="flex items-center gap-1.5 text-xs font-semibold text-td-muted hover:text-td-navy transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 shrink-0 transition-transform ${showOthers ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Bekijk andere taken ({otherGroups.reduce((sum, g) => sum + g.tasks.length, 0)})
          </button>

          {showOthers && (
            <div className="mt-3 space-y-4">
              {otherGroups.map((group) => (
                <div key={group.sortKey}>
                  {group.sortKey === '9999-99-99' ? (
                    <h4 className="text-xs font-bold text-td-muted uppercase tracking-wide border-b border-gray-100 pb-1 mb-3">
                      {group.label}
                    </h4>
                  ) : (
                    <div className="border-b border-gray-100 pb-1 mb-3">
                      <TaskDate date={group.label} />
                    </div>
                  )}
                  <div className="space-y-3">
                    {group.tasks.map((task) => (
                      <div key={task.id}>
                        <p className="text-sm font-semibold text-td-navy mb-1">{task.name}</p>
                        {canEdit ? (
                          <TaskDescriptionEditable task={task} projectId={projectId} />
                        ) : (
                          <SafeHtml html={task.description || ''} className="text-sm text-td-navy" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ─── Section 2b: Installateurs ─── */

/** Installers of the shown day (the same task as Taakomschrijving). */
export function InstallateursSection({ detail, task: targetTask }: { detail: ProjectDetail; task: ProjectTask | null }) {
  const installerIds = targetTask?.x_studio_installateur ?? [];
  if (installerIds.length === 0) return null;

  const names = installerIds.map(
    (id) => detail.installerNames[String(id)] ?? 'Onbekend',
  );

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h3 className="text-[10px] font-bold text-td-muted uppercase tracking-wide mb-2">Installateurs</h3>
      <div className="space-y-1">
        {names.map((name, i) => (
          <p key={i} className="text-sm font-semibold text-td-navy">{name}</p>
        ))}
      </div>
    </section>
  );
}
